import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import mailClientAPI from "../../api/mail-client-api";
import { useUser } from "../../context/UserContext";

const BRAND_COLOR = '#623CE7';
const BRAND_COLOR_HOVER = '#4f2fc2';
const BRAND_COLOR_LIGHT = '#F4F1FD';

const FADE_IN_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const STAGGER_CHILDREN = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const VALUE_POINTS = [
  { icon: "language", text: "Custom domain email" },
  { icon: "bolt", text: "AI assistance while writing" },
  { icon: "check_circle", text: "Reliable delivery and uptime" },
  { icon: "phone_android", text: "Works across devices and teams" },
];

// VisualMockup Component
const VisualMockup = () => {
  const [phase, setPhase] = useState('compose');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    let timer1, timer2, timer3;

    const loop = () => {
      if (!isMountedRef.current) return;
      
      // Phase 1: Compose (starts at 0s)
      setPhase('compose');

      // Phase 2: Transit (starts at 4s)
      timer1 = setTimeout(() => {
        if (isMountedRef.current) setPhase('transit');
      }, 4000);

      // Phase 3: Inbox (starts at 5.5s)
      timer2 = setTimeout(() => {
        if (isMountedRef.current) setPhase('inbox');
      }, 5500);

      // Reset loop (starts at 10s)
      timer3 = setTimeout(() => {
        if (isMountedRef.current) loop();
      }, 10000);
    };

    loop();

    return () => {
      isMountedRef.current = false;
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
      if (timer3) clearTimeout(timer3);
    };
  }, []);

  return (
    <div className="relative w-full max-w-[340px] lg:max-w-[380px] xl:max-w-[420px] aspect-[4/3] mx-auto">
      
      {/* Decorative Background Blobs */}
      <motion.div 
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.3, 0.2] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-0 right-0 w-64 h-64 bg-indigo-400 rounded-full blur-3xl opacity-20 -z-10"
      />
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 10, repeat: Infinity, delay: 1 }}
        className="absolute bottom-0 left-0 w-56 h-56 bg-purple-400 rounded-full blur-3xl opacity-10 -z-10"
      />

      <div className="relative w-full h-full">
        <AnimatePresence mode="wait">
          
          {/* === SCENE 1: COMPOSE === */}
          {phase === 'compose' && (
            <motion.div
              key="compose"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -10, filter: 'blur(4px)' }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="h-10 lg:h-12 border-b border-slate-100 flex items-center px-4 lg:px-5 justify-between bg-slate-50/50">
                <div className="flex items-center gap-2">
                   <div className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full bg-slate-200" />
                   <div className="h-1.5 lg:h-2 w-20 lg:w-24 bg-slate-200 rounded-full" />
                </div>
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-red-400/20" />
                  <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-amber-400/20" />
                  <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-green-400/20" />
                </div>
              </div>

              {/* Body */}
              <div className="p-4 lg:p-6 space-y-4 lg:space-y-6 flex-1">
                <div className="space-y-2 lg:space-y-3">
                  <div className="h-1.5 lg:h-2 w-16 bg-slate-100 rounded-full" />
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1, delay: 0.5, ease: "linear" }}
                    className="h-1.5 lg:h-2 bg-slate-200 rounded-full" 
                  />
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: "85%" }}
                    transition={{ duration: 0.8, delay: 1.5, ease: "linear" }}
                    className="h-1.5 lg:h-2 bg-slate-200 rounded-full" 
                  />
                  
                  {/* AI Suggestion */}
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.5 }}
                    className="flex items-center gap-2 mt-2"
                  >
                    <div className="p-0.5 lg:p-1 rounded bg-indigo-50 text-indigo-600">
                      <span className="material-icons-outlined text-xs lg:text-sm">auto_awesome</span>
                    </div>
                    <div className="h-1.5 lg:h-2 w-24 lg:w-32 bg-indigo-100 rounded-full" />
                  </motion.div>
                </div>

                <div className="mt-auto flex justify-end">
                  <motion.div
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 0.95, 1] }}
                    transition={{ delay: 3.5, duration: 0.2 }}
                    className="px-3 py-1.5 lg:px-4 lg:py-2 bg-[#623CE7] rounded-lg shadow-lg shadow-indigo-200 flex items-center gap-2"
                  >
                    <span className="text-[10px] lg:text-xs font-semibold text-white">Send</span>
                    <span className="material-icons-outlined text-xs lg:text-sm text-white">send</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}

          {/* === SCENE 2: TRANSIT === */}
          {phase === 'transit' && (
            <motion.div
              key="transit"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              {/* Path Line */}
              <svg className="absolute inset-0 w-full h-full overflow-visible">
                <motion.path
                  d="M 50,200 Q 210,50 370,200"
                  fill="none"
                  stroke="#623CE7"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="10 10"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.3 }}
                  transition={{ duration: 1 }}
                />
              </svg>

              {/* Flying Envelope */}
              <motion.div
                initial={{ x: -150, y: 50, scale: 0.5, opacity: 0 }}
                animate={{ x: 0, y: 0, scale: 1.2, opacity: 1 }}
                exit={{ x: 150, y: -50, scale: 0.5, opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                className="z-10 relative"
              >
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-indigo-100 text-[#623CE7]">
                  <span className="material-icons-outlined text-2xl lg:text-3xl">mail</span>
                  <motion.div 
                    className="absolute inset-0 bg-indigo-400 rounded-2xl opacity-20"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  />
                </div>
              </motion.div>

              {/* Network Nodes */}
              <motion.div 
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute left-1/4 top-1/2 -mt-10 bg-white p-1.5 lg:p-2 rounded-full shadow-md text-emerald-500"
              >
                <span className="material-icons-outlined text-sm lg:text-base">check_circle</span>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="absolute right-1/4 top-1/2 mt-8 bg-white p-1.5 lg:p-2 rounded-full shadow-md text-amber-500"
              >
                <span className="material-icons-outlined text-sm lg:text-base">bolt</span>
              </motion.div>
            </motion.div>
          )}

          {/* === SCENE 3: INBOX === */}
          {phase === 'inbox' && (
            <motion.div
              key="inbox"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 overflow-hidden flex flex-col"
            >
              {/* Inbox Header */}
              <div className="h-10 lg:h-14 border-b border-slate-100 flex items-center px-4 lg:px-6 justify-between bg-white">
                <span className="font-bold text-sm lg:text-base text-slate-800">Inbox</span>
                <div className="flex gap-2">
                  <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-slate-300" />
                  <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-slate-300" />
                </div>
              </div>

              {/* Email List */}
              <div className="p-3 lg:p-4 space-y-2 lg:space-y-3">
                {/* New Item (Animates In) */}
                <motion.div
                  initial={{ height: 0, opacity: 0, x: -20 }}
                  animate={{ height: 'auto', opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
                  className="bg-indigo-50/50 p-2 lg:p-3 rounded-xl border border-indigo-100 relative overflow-hidden"
                >
                   {/* Highlight Indicator */}
                   <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#623CE7]" />
                   
                   <div className="flex items-start gap-2 lg:gap-3 pl-2">
                     <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-[10px] lg:text-xs shrink-0">
                        JD
                     </div>
                     <div className="flex-1 space-y-1 lg:space-y-1.5">
                       <div className="flex justify-between items-center">
                         <div className="h-1.5 lg:h-2 w-16 lg:w-20 bg-slate-800 rounded-full opacity-20" />
                         <span className="text-[9px] lg:text-[10px] text-indigo-600 font-medium">Just now</span>
                       </div>
                       <div className="h-1.5 lg:h-2 w-full bg-slate-400 rounded-full opacity-10" />
                       <div className="flex gap-2 mt-1">
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 1 }}
                            className="px-1.5 py-0.5 bg-white rounded border border-indigo-100 text-[8px] lg:text-[9px] font-medium text-indigo-600 flex items-center gap-1 shadow-sm"
                          >
                            <span className="material-icons-outlined text-xs">auto_awesome</span>
                            <span>Action required</span>
                          </motion.div>
                       </div>
                     </div>
                   </div>
                </motion.div>

                {/* Existing Items */}
                {[1, 2].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ y: 0 }}
                    animate={{ y: 0 }}
                    className="bg-white p-2 lg:p-3 rounded-xl border border-slate-100 opacity-60"
                  >
                    <div className="flex items-start gap-2 lg:gap-3">
                      <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">
                        <span className="material-icons-outlined text-xs lg:text-sm">person</span>
                      </div>
                      <div className="flex-1 space-y-1.5 lg:space-y-2">
                         <div className="h-1.5 lg:h-2 w-20 lg:w-24 bg-slate-200 rounded-full" />
                         <div className="h-1.5 lg:h-2 w-full bg-slate-100 rounded-full" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

function MailLogin() {
  const { loadUserInfo } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await mailClientAPI.login(email, password);
      
      // Load user details
      setTimeout(() => {
        loadUserInfo();
      }, 1000);

      if (response.Output?.token) {
        // Navigate to inbox
        navigate("/mailboxes/INBOX");
      } else {
        throw new Error("Login failed - no token received");
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.response?.data?.Output?.message || error.message || "Login failed. Please check your credentials.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Grigora Mail</title>
      </Helmet>
      <div className="h-screen w-full bg-slate-50 flex flex-col lg:flex-row overflow-hidden font-sans">
        
        {/* LEFT SECTION: Login Interaction */}
        <div className="w-full lg:w-[48%] xl:w-[45%] flex flex-col justify-center px-6 sm:px-12 lg:pl-24 lg:pr-12 xl:pl-48 relative z-10 bg-slate-50/50">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={STAGGER_CHILDREN}
            className="w-full max-w-[440px] mx-auto flex flex-col justify-center h-full py-4 lg:py-6"
          >
            {/* Logo */}
            <motion.div variants={FADE_IN_VARIANTS} className="mb-4 lg:mb-6 xl:mb-8 pl-1">
              <div className="flex items-center select-none">
                <div 
                  className="w-12 h-12 flex items-center justify-center mr-3"
                >
                  <img src="/logo512.png" alt="Grigora" className="w-full h-full object-contain" />
                </div>
                <span className="font-bold text-2xl tracking-tight text-slate-900">Grigora</span>
              </div>
            </motion.div>

            {/* Card Container */}
            <motion.div 
              variants={FADE_IN_VARIANTS}
              className="bg-white p-6 lg:p-8 xl:p-10 rounded-3xl border border-slate-100 shadow-[0_20px_40px_rgba(0,0,0,0.04)]"
            >
              {/* Header */}
              <div className="mb-5 lg:mb-6 xl:mb-8 space-y-2 lg:space-y-3">
                <span className="inline-block text-xs xl:text-sm font-semibold text-indigo-600 tracking-wide uppercase bg-indigo-50 px-2.5 py-0.5 xl:px-3 xl:py-1 rounded-full">
                  Welcome back
                </span>
                <h1 className="text-2xl xl:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                  Sign in to your <br/> business email
                </h1>
                <p className="text-slate-500 text-sm xl:text-base leading-relaxed">
                  Access your professional inbox and conversations in seconds.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5 xl:space-y-6">
                {/* Email Input */}
                <div className="space-y-1.5 xl:space-y-2">
                  <label className="block text-sm font-semibold text-slate-700 ml-1">
                    Email address
                  </label>
                  <div className="relative group">
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@yourdomain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setIsFocused({ ...isFocused, email: true })}
                      onBlur={() => setIsFocused({ ...isFocused, email: false })}
                      disabled={isLoading}
                      className="w-full px-4 py-3 xl:px-5 xl:py-4 text-base rounded-xl border bg-slate-50/50 transition-all duration-300 outline-none placeholder:text-slate-400 border-slate-200 hover:border-slate-300 hover:bg-white"
                      style={{
                        borderColor: isFocused.email ? BRAND_COLOR : undefined,
                        boxShadow: isFocused.email ? `0 0 0 4px ${BRAND_COLOR}15` : undefined,
                        backgroundColor: isFocused.email ? '#fff' : undefined
                      }}
                    />
                  </div>
                </div>
                
                {/* Password Input */}
                <div className="space-y-1.5 xl:space-y-2">
                  <label className="block text-sm font-semibold text-slate-700 ml-1">
                    Password
                  </label>
                  <div className="relative group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setIsFocused({ ...isFocused, password: true })}
                      onBlur={() => setIsFocused({ ...isFocused, password: false })}
                      disabled={isLoading}
                      className="w-full px-4 py-3 xl:px-5 xl:py-4 text-base rounded-xl border bg-slate-50/50 transition-all duration-300 outline-none placeholder:text-slate-400 border-slate-200 hover:border-slate-300 hover:bg-white pr-12"
                      style={{
                        borderColor: isFocused.password ? BRAND_COLOR : undefined,
                        boxShadow: isFocused.password ? `0 0 0 4px ${BRAND_COLOR}15` : undefined,
                        backgroundColor: isFocused.password ? '#fff' : undefined
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-lg focus:outline-none focus:bg-slate-100"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <span className="material-icons-outlined text-xl">
                        {showPassword ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                  </div>
                  <div className="flex justify-end pt-0.5">
                    <button 
                      type="button" 
                      className="text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors"
                      onClick={() => window.open("https://help.grigora.co", "_blank")}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 lg:py-3.5 xl:py-4 px-6 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-lg shadow-indigo-200 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-200/50 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none mt-2"
                  style={{ backgroundColor: isLoading ? '#8875e0' : BRAND_COLOR }}
                  onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = BRAND_COLOR_HOVER }}
                  onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = BRAND_COLOR }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <span>Sign in</span>
                  )}
                </button>
              </form>

              {/* Helper Link */}
              <div className="mt-5 lg:mt-6 xl:mt-8 text-center">
                <p className="text-slate-600 text-sm">
                  New to Grigora?{' '}
                  <a href="https://build.grigora.co" target="_blank" className="font-semibold text-indigo-600 hover:text-indigo-700 hover:underline transition-colors">
                    Create an account
                  </a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* RIGHT SECTION: Value & Trust */}
        <div 
          className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-center items-center px-10 xl:px-24 relative"
          style={{ backgroundColor: BRAND_COLOR_LIGHT }}
        >
          <div className="w-full max-w-xl flex flex-col gap-6 xl:gap-10">
            
            {/* Header & Points */}
            <div className="flex flex-col gap-5 xl:gap-8 text-center lg:text-left">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-900 leading-tight"
              >
                Email that works the way <br />
                <span className="text-indigo-600">your business does.</span>
              </motion.h2>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-y-2.5 gap-x-6 lg:gap-y-3 xl:gap-y-4"
              >
                {VALUE_POINTS.map((point, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex p-1.5 rounded-full bg-white text-indigo-600 shadow-sm ring-1 ring-slate-100 shrink-0">
                      <span className="material-icons-outlined text-base leading-none">{point.icon}</span>
                    </div>
                    <span className="text-sm xl:text-base font-medium text-slate-700 whitespace-nowrap">{point.text}</span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Animated Visual */}
            <div className="w-full flex items-center justify-center">
              <VisualMockup />
            </div>

            {/* Trust Section */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col items-center gap-2 lg:gap-3 w-full"
            >
              <div className="flex items-center justify-center gap-6 lg:gap-8 opacity-90">
                {/* Mock G2 Badge */}
                <Link to="https://www.g2.com/products/grigora/reviews" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 group cursor-pointer">
                  <div className="bg-[#FF4F00] w-8 h-8 lg:w-9 lg:h-9 rounded-md flex items-center justify-center text-white font-bold text-xs shadow-sm group-hover:scale-105 transition-transform">
                    G2
                  </div>
                  <div className="flex flex-col">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <span key={i} className="material-icons-outlined text-[#FF4F00] text-xs lg:text-sm">star</span>
                      ))}
                    </div>
                    <span className="text-[9px] lg:text-[10px] font-bold text-slate-700 uppercase tracking-wide mt-0.5">High Performer</span>
                  </div>
                </Link>

                <div className="h-6 lg:h-8 w-px bg-slate-300/50" />

                {/* Mock Trustpilot Badge */}
                <Link to="https://www.trustpilot.com/review/grigora.co" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 group cursor-pointer">
                  <div className="bg-[#00b67a] w-8 h-8 lg:w-9 lg:h-9 rounded-full flex items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform">
                    <span className="material-icons-outlined text-sm lg:text-base">star</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-slate-800 leading-none">Trustpilot</span>
                    <div className="flex gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="w-1.5 h-1.5 lg:w-2 lg:h-2 bg-[#00b67a] rounded-[1px]" />
                      ))}
                    </div>
                  </div>
                </Link>
              </div>
              <p className="text-xs lg:text-sm font-medium text-slate-500 tracking-tight text-center">
                Trusted by founders, teams, and creators worldwide
              </p>
            </motion.div>

          </div>
          
          {/* Subtle decorative background elements */}
          <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-[15%] right-[-10%] w-[400px] h-[400px] lg:w-[500px] lg:h-[500px] bg-indigo-200/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-[10%] left-[-10%] w-[300px] h-[300px] lg:w-[400px] lg:h-[400px] bg-purple-200/20 rounded-full blur-[80px]" />
          </div>
        </div>

        {/* Mobile-only background overlay */}
        <div className="lg:hidden fixed bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
      </div>
    </>
  );
}

export default MailLogin;


